/**
 * Keeps admin-only `_Fulfillment Type` on cart lines and mirrors a short summary
 * into the order note.
 *
 * Types:
 * - Full Box
 * - Pouch (single sample pouch)
 * - Sampler Trio (Pouch) - multi-pouch pack named Sampler Trio
 *
 * Sampler Trio is a Shopify bundle: component lines often do not inherit the
 * parent add-to-cart properties, so we detect bundle relationships and stamp them.
 */

const PROPERTY_KEY = '_Fulfillment Type';
const NOTE_PREFIX = 'Fulfillment:';

/** @type {Promise<void> | null} */
let syncPromise = null;
let syncQueued = false;

/**
 * @param {Record<string, string> | null | undefined} properties
 * @param {string} key
 * @returns {string}
 */
function getPropertyValue(properties, key) {
  if (!properties || typeof properties !== 'object') return '';
  const value = properties[key];
  return value == null ? '' : String(value).trim();
}

/**
 * @param {object} item
 * @returns {string}
 */
function getItemHandle(item) {
  return String(item?.handle || item?.product_handle || '').toLowerCase();
}

/**
 * @param {object} item
 * @returns {boolean}
 */
function isSamplerTrioProduct(item) {
  const handle = getItemHandle(item);
  const title = String(item?.product_title || item?.title || '').toLowerCase();
  return handle.includes('sampler-trio') || title.includes('sampler trio');
}

/**
 * @param {object} item
 * @param {object[]} allItems
 * @returns {boolean}
 */
function isSamplerTrioComponent(item, allItems) {
  if (!item || !Array.isArray(allItems)) return false;

  // Native Shopify bundle parent relationship (AJAX cart)
  const parentKey =
    item.parent_relationship?.parent_key ||
    item.parent_relationship?.parent ||
    item.parent_id ||
    null;

  if (parentKey) {
    const parent = allItems.find(
      (candidate) =>
        candidate.key === parentKey ||
        String(candidate.id) === String(parentKey) ||
        String(candidate.key).startsWith(String(parentKey))
    );
    if (parent && isSamplerTrioProduct(parent)) return true;
  }

  // Parent line that lists this item as a component
  for (const candidate of allItems) {
    if (!isSamplerTrioProduct(candidate)) continue;
    const components = candidate.item_components || candidate.components || [];
    if (!Array.isArray(components) || !components.length) continue;

    const match = components.some((component) => {
      return (
        component.key === item.key ||
        String(component.id) === String(item.id) ||
        String(component.variant_id) === String(item.variant_id)
      );
    });
    if (match) return true;
  }

  // Fallback: common property labels Shopify / apps may attach
  const properties = item.properties || {};
  for (const [key, value] of Object.entries(properties)) {
    const keyLower = String(key).toLowerCase();
    const valueLower = String(value || '').toLowerCase();
    if (
      (keyLower.includes('part of') || keyLower.includes('bundle')) &&
      valueLower.includes('sampler')
    ) {
      return true;
    }
    if (valueLower.includes('sampler trio') || valueLower.includes('sampler-trio')) {
      return true;
    }
  }

  return false;
}

/**
 * @param {object} item
 * @param {object[]} [allItems]
 * @returns {string}
 */
export function resolveFulfillmentType(item, allItems = []) {
  const properties = item?.properties || {};
  const existing = getPropertyValue(properties, PROPERTY_KEY);

  // Sampler Trio is a multi-pouch pack - keep it distinct from single Pouch / Full Box
  if (isSamplerTrioProduct(item) || isSamplerTrioComponent(item, allItems)) {
    return 'Sampler Trio (Pouch)';
  }

  if (existing === 'Sampler Trio') return 'Sampler Trio (Pouch)';
  if (existing) return existing;

  const handle = getItemHandle(item);
  const title = String(item?.title || item?.product_title || '').toLowerCase();

  if (handle.includes('pouch') || title.includes('sample pouch') || title.includes('pouch')) {
    return 'Pouch';
  }

  return 'Full Box';
}

/**
 * @returns {Promise<object>}
 */
async function fetchCart() {
  const response = await fetch('/cart.js', {
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error('Unable to load cart');
  return response.json();
}

/**
 * @param {object} item
 * @param {string} fulfillmentType
 */
async function updateLineFulfillmentProperty(item, fulfillmentType) {
  const nextProperties = { ...(item.properties || {}) };
  nextProperties[PROPERTY_KEY] = fulfillmentType;

  const response = await fetch('/cart/change.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      id: item.key,
      quantity: item.quantity,
      properties: nextProperties,
    }),
  });

  if (!response.ok) {
    throw new Error(`Unable to update fulfillment property for ${item.key}`);
  }

  return response.json();
}

/**
 * @param {string} existingNote
 * @param {string[]} types
 * @returns {string}
 */
function buildFulfillmentNote(existingNote, types) {
  const uniqueTypes = [...new Set(types.filter(Boolean))];
  const fulfillmentLine = uniqueTypes.length
    ? `${NOTE_PREFIX} ${uniqueTypes.join(', ')}`
    : '';

  const otherLines = String(existingNote || '')
    .split('\n')
    .map((line) => line.trimEnd())
    .filter((line) => {
      const trimmed = line.trim();
      return trimmed !== '' && !trimmed.startsWith(NOTE_PREFIX);
    });

  if (!fulfillmentLine) {
    return otherLines.join('\n').trim();
  }

  if (!otherLines.length) {
    return fulfillmentLine;
  }

  return `${otherLines.join('\n')}\n${fulfillmentLine}`.trim();
}

/**
 * @param {string} note
 */
async function updateCartNote(note) {
  const response = await fetch('/cart/update.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ note }),
  });

  if (!response.ok) {
    throw new Error('Unable to update cart note');
  }

  return response.json();
}

/**
 * Stamp missing line properties + refresh order note.
 */
export async function syncFulfillmentToCart() {
  if (syncPromise) {
    syncQueued = true;
    return syncPromise;
  }

  syncPromise = (async () => {
    do {
      syncQueued = false;
      const cart = await fetchCart();
      const items = Array.isArray(cart.items) ? cart.items : [];

      for (const item of items) {
        const type = resolveFulfillmentType(item, items);
        const current = getPropertyValue(item.properties, PROPERTY_KEY);
        if (current === type) continue;

        try {
          await updateLineFulfillmentProperty(item, type);
        } catch (error) {
          console.warn(error);
        }
      }

      const refreshed = await fetchCart();
      const refreshedItems = Array.isArray(refreshed.items) ? refreshed.items : [];
      const types = refreshedItems.map((item) => resolveFulfillmentType(item, refreshedItems));
      const nextNote = buildFulfillmentNote(refreshed.note || '', types);

      if ((refreshed.note || '').trim() !== nextNote.trim()) {
        await updateCartNote(nextNote);

        const noteField = document.querySelector('#cart-note, textarea[name="note"]');
        if (noteField instanceof HTMLTextAreaElement) {
          noteField.value = nextNote;
        }
      }
    } while (syncQueued);
  })();

  try {
    await syncPromise;
  } finally {
    syncPromise = null;
  }
}

function scheduleSync() {
  window.setTimeout(() => {
    syncFulfillmentToCart().catch((error) => {
      console.warn('Fulfillment sync failed', error);
    });
  }, 350);
}

function initBestiesFulfillment() {
  if (window.__bestiesFulfillmentBound) return;
  window.__bestiesFulfillmentBound = true;

  document.addEventListener('cart:update', scheduleSync);
  document.addEventListener('cart:updated', scheduleSync);

  if (document.querySelector('cart-items-component, cart-drawer-component')) {
    scheduleSync();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBestiesFulfillment);
} else {
  initBestiesFulfillment();
}
